import React, { useState, useEffect, useRef } from "react";
import "./Footer.css";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../../services/api";
import { Scenario, Content } from "../../types";

interface SearchResult {
  type: "scenario" | "content";
  id: string;
  title: string;
  path: string;
}

export function Footer() {
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [profileOpen, setProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [allItems, setAllItems] = useState<SearchResult[]>([]);
  const [itemsLoaded, setItemsLoaded] = useState(false);

  const profileRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setProfileOpen(false); setSearchOpen(false); }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEsc);
    return () => { document.removeEventListener("mousedown", handleClick); document.removeEventListener("keydown", handleEsc); };
  }, []);

  const loadSearchItems = async () => {
    if (itemsLoaded) return;
    try {
      const [scenariosResult, contentResult] = await Promise.all([
        apiGet<{ items: Scenario[] }>("/api/scenarios"),
        apiGet<{ items: Content[] }>("/api/content"),
      ]);
      const items: SearchResult[] = [
        ...scenariosResult.items.map((s) => ({ type: "scenario" as const, id: s.scenarioId, title: s.name, path: `/practice?scenario=${s.scenarioId}` })),
        ...contentResult.items.filter((c) => c.status === "published").map((c) => ({ type: "content" as const, id: c.contentId, title: c.title, path: "/learn" })),
      ];
      setAllItems(items);
      setItemsLoaded(true);
    } catch {
      // Search is best-effort
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const q = searchQuery.toLowerCase();
    setSearchResults(allItems.filter((item) => item.title.toLowerCase().includes(q)).slice(0, 8));
  }, [searchQuery, allItems]);

  const handleSearchFocus = () => {
    setSearchOpen(true);
    loadSearchItems();
  };

  const handleResultClick = (result: SearchResult) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(result.path);
  };

  const initials = user?.name ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "?";

  return (
    <footer className="protrain-footer">
      <div className="protrain-footer-left" ref={profileRef}>
        {user && (
          <>
            <button className="protrain-footer-profile-btn" onClick={() => setProfileOpen(!profileOpen)}>
              <span className="protrain-footer-avatar">{initials}</span>
              <span className="protrain-footer-name">{user.name}</span>
              <span className="protrain-footer-caret">{profileOpen ? "▴" : "▾"}</span>
            </button>
            {profileOpen && (
              <div className="protrain-footer-profile-menu">
                <div className="protrain-footer-profile-menu-item" onClick={toggleTheme}>
                  <span>{theme === "dark" ? "☀️" : "🌙"}</span>
                  <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
                </div>
                <div className="protrain-footer-profile-menu-divider" />
                <div className="protrain-footer-profile-menu-item protrain-footer-profile-menu-item--danger" onClick={signOut}>
                  <span>🚪</span>
                  <span>Sign Out</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="protrain-footer-center" ref={searchRef}>
        <div className="protrain-footer-search">
          <input
            type="text"
            placeholder="Search scenarios, content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleSearchFocus}
          />
          {searchOpen && searchQuery && (
            <div className="protrain-footer-search-dropdown">
              {searchResults.length === 0 ? (
                <div className="protrain-footer-search-empty">No results found</div>
              ) : (
                <>
                  {searchResults.some((r) => r.type === "scenario") && (
                    <div className="protrain-footer-search-group">
                      <span className="protrain-footer-search-group-label">Scenarios</span>
                      {searchResults.filter((r) => r.type === "scenario").map((r) => (
                        <button key={r.id} className="protrain-footer-search-result" onClick={() => handleResultClick(r)}>
                          🎭 {r.title}
                        </button>
                      ))}
                    </div>
                  )}
                  {searchResults.some((r) => r.type === "content") && (
                    <div className="protrain-footer-search-group">
                      <span className="protrain-footer-search-group-label">Content</span>
                      {searchResults.filter((r) => r.type === "content").map((r) => (
                        <button key={r.id} className="protrain-footer-search-result" onClick={() => handleResultClick(r)}>
                          📱 {r.title}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="protrain-footer-right">
        <span className="protrain-footer-app-name">ProTrain</span>
      </div>
    </footer>
  );
}
